import { useState, useEffect } from 'react';
import { Brain, Edit, Save, X, ChevronDown, ChevronUp, Trash2, Plus } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { api } from '../../services/api';
import type { AdminQuestionModule, AdminQuizQuestion } from '../../types';

export default function AdminQuestions() {
  const { theme } = useTheme();
  const [questionModules, setQuestionModules] = useState<AdminQuestionModule[]>([]);
  const [filteredModules, setFilteredModules] = useState<AdminQuestionModule[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingQuestion, setEditingQuestion] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<AdminQuizQuestion>>({});
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});
  const [showCreateForm, setShowCreateForm] = useState<string | null>(null);
  const [newQuestion, setNewQuestion] = useState<Omit<AdminQuizQuestion, 'id'>>({
    question: '',
    choices: { A: '', B: '', C: '', D: '' },
    correct_answer: 'A',
    explanation: '',
    module_code: '',
    course_id: null,
    difficulty: 'facile'
  });

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const questionsData = await api.getAllQuizQuestions();
      setQuestionModules(questionsData);
      setFilteredModules(questionsData);
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuestion = (question: AdminQuizQuestion) => {
    setEditingQuestion(question.id);
    setEditData({ ...question });
  };

  const handleSaveQuestion = async (questionId: string) => {
    try {
      await api.updateQuizQuestion(questionId, editData);

      // Update local state
      setQuestionModules(questionModules.map(module => ({
        ...module,
        questions: module.questions.map(question =>
          question.id === questionId ? { ...question, ...editData } as AdminQuizQuestion : question
        )
      })));

      setEditingQuestion(null);
      setEditData({});
    } catch (error) {
      console.error('Error updating question:', error);
      alert('Erreur lors de la mise à jour de la question');
    }
  };

  const handleDeleteQuestion = async (questionId: string, moduleCode: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette question ?')) {
      return;
    }

    try {
      await api.deleteQuizQuestion(questionId);

      // Update local state
      setQuestionModules(questionModules.map(module =>
        module.module_code === moduleCode
          ? { ...module, questions: module.questions.filter(q => q.id !== questionId) }
          : module
      ).filter(module => module.questions.length > 0));

    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Erreur lors de la suppression de la question');
    }
  };

  const handleCreateQuestion = async (moduleCode: string) => {
    try {
      const questionData = { ...newQuestion, module_code: moduleCode };
      const createdQuestion = await api.createQuizQuestion(questionData);

      // Update local state
      setQuestionModules(questionModules.map(module =>
        module.module_code === moduleCode
          ? { ...module, questions: [...module.questions, createdQuestion] }
          : module
      ));

      // Reset form
      setNewQuestion({
        question: '',
        choices: { A: '', B: '', C: '', D: '' },
        correct_answer: 'A',
        explanation: '',
        module_code: '',
        course_id: null,
        difficulty: 'facile'
      });
      setShowCreateForm(null);

    } catch (error) {
      console.error('Error creating question:', error);
      alert('Erreur lors de la création de la question');
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setFilteredModules(questionModules);
      return;
    }

    const filtered = questionModules.map(module => ({
      ...module,
      questions: module.questions.filter(question =>
        question.question.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })).filter(module => module.questions.length > 0);

    setFilteredModules(filtered);
  };

  useEffect(() => {
    handleSearch();
  }, [searchTerm, questionModules]);

  const toggleModule = (moduleCode: string) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleCode]: !prev[moduleCode]
    }));
  };

  const getTotalQuestions = () => {
    return filteredModules.reduce((total, module) => total + module.questions.length, 0);
  };

  const updateEditData = (field: string, value: any) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const updateEditChoice = (letter: string, value: string) => {
    setEditData(prev => ({
      ...prev,
      choices: { ...prev.choices, [letter]: value }
    }));
  };

  const updateNewQuestion = (field: string, value: any) => {
    setNewQuestion(prev => ({ ...prev, [field]: value }));
  };

  const updateNewChoice = (letter: string, value: string) => {
    setNewQuestion(prev => ({
      ...prev,
      choices: { ...prev.choices, [letter]: value }
    }));
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className={`w-8 h-8 border-4 border-${theme.primary} border-t-transparent rounded-full animate-spin mx-auto mb-4`} />
        <p className="text-gray-600">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">Questions QCM ({getTotalQuestions()})</h2>

      {/* Search */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Rechercher dans les questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setSearchTerm('')}
            className={`px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors ${
              !searchTerm ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={!searchTerm}
          >
            Effacer
          </button>
        </div>
        {searchTerm && (
          <p className="text-sm text-gray-600 mt-2">
            {getTotalQuestions()} question(s) trouvée(s) pour "{searchTerm}"
          </p>
        )}
      </div>

      {questionModules.length === 0 ? (
        <p className="text-gray-500 text-center py-8">Aucune question trouvée</p>
      ) : (
        filteredModules.map((module) => (
          <div key={module.module_code} className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleModule(module.module_code)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <span className={`px-3 py-1 bg-${theme.secondary} text-${theme.text} text-sm font-medium rounded mr-3`}>
                  {module.module_code}
                </span>
                <h3 className="font-semibold text-left">Module {module.module_code}</h3>
              </div>
              <div className="flex items-center">
                <span className="text-sm text-gray-500 mr-2">
                  {module.questions.length} questions
                </span>
                {expandedModules[module.module_code] ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </button>

            {expandedModules[module.module_code] && (
              <div className="border-t border-gray-100">
                {/* Add Question Button */}
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                  <button
                    onClick={() => setShowCreateForm(module.module_code)}
                    className={`flex items-center px-4 py-2 bg-${theme.primary} text-white rounded-lg hover:opacity-90 transition-opacity`}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter une question
                  </button>
                </div>

                {/* Create Question Form */}
                {showCreateForm === module.module_code && (
                  <div className="px-6 py-4 border-b border-gray-100 bg-blue-50">
                    <h4 className="font-medium mb-4">Nouvelle question pour le module {module.module_code}</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                        <textarea
                          value={newQuestion.question}
                          onChange={(e) => updateNewQuestion('question', e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          rows={2}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {['A', 'B', 'C', 'D'].map(letter => (
                          <div key={letter}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Choix {letter}
                            </label>
                            <input
                              type="text"
                              value={newQuestion.choices[letter] || ''}
                              onChange={(e) => updateNewChoice(letter, e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                        ))}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Bonne réponse</label>
                          <select
                            value={newQuestion.correct_answer}
                            onChange={(e) => updateNewQuestion('correct_answer', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                          >
                            <option value="A">A</option>
                            <option value="B">B</option>
                            <option value="C">C</option>
                            <option value="D">D</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Difficulté</label>
                          <select
                            value={newQuestion.difficulty}
                            onChange={(e) => updateNewQuestion('difficulty', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                          >
                            <option value="facile">Facile</option>
                            <option value="moyen">Moyen</option>
                            <option value="difficile">Difficile</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Explication</label>
                        <textarea
                          value={newQuestion.explanation}
                          onChange={(e) => updateNewQuestion('explanation', e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          rows={3}
                        />
                      </div>

                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleCreateQuestion(module.module_code)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          Créer la question
                        </button>
                        <button
                          onClick={() => setShowCreateForm(null)}
                          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Questions List */}
                {module.questions.map((question) => (
                  <div key={question.id} className="px-6 py-4 border-b last:border-b-0 border-gray-100">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded`}>
                            {question.difficulty}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {editingQuestion === question.id ? (
                          <>
                            <button
                              onClick={() => handleSaveQuestion(question.id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded"
                            >
                              <Save className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingQuestion(null);
                                setEditData({});
                              }}
                              className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEditQuestion(question)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteQuestion(question.id, module.module_code)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {editingQuestion === question.id ? (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                          <textarea
                            value={editData.question || ''}
                            onChange={(e) => updateEditData('question', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                            rows={2}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          {['A', 'B', 'C', 'D'].map(letter => (
                            <div key={letter}>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Choix {letter}
                              </label>
                              <input
                                type="text"
                                value={editData.choices?.[letter] || ''}
                                onChange={(e) => updateEditChoice(letter, e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg"
                              />
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Bonne réponse</label>
                            <select
                              value={editData.correct_answer || 'A'}
                              onChange={(e) => updateEditData('correct_answer', e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-lg"
                            >
                              <option value="A">A</option>
                              <option value="B">B</option>
                              <option value="C">C</option>
                              <option value="D">D</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Difficulté</label>
                            <select
                              value={editData.difficulty || 'facile'}
                              onChange={(e) => updateEditData('difficulty', e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-lg"
                            >
                              <option value="facile">Facile</option>
                              <option value="moyen">Moyen</option>
                              <option value="difficile">Difficile</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Explication</label>
                          <textarea
                            value={editData.explanation || ''}
                            onChange={(e) => updateEditData('explanation', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg"
                            rows={3}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <h4 className="font-medium">{question.question}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {Object.entries(question.choices).map(([letter, choice]) => (
                            choice && (
                              <div
                                key={letter}
                                className={`p-2 rounded text-sm ${
                                  letter === question.correct_answer
                                    ? 'bg-green-100 text-green-800 font-medium'
                                    : 'bg-gray-50 text-gray-700'
                                }`}
                              >
                                {letter}. {choice}
                              </div>
                            )
                          ))}
                        </div>
                        <div className="bg-blue-50 p-2 rounded">
                          <p className="text-sm text-blue-800">
                            <strong>Explication:</strong> {question.explanation}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}